import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Assessment_Status_Enum,
  InsertInternalAuditReportMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * tRPC input type for insertInternalAuditReport. The report register is a
 * near-1:1 twin of assessment and shares the InsertAssessmentInput wire shape,
 * so the Status field is the assessment status enum (the DB stores an
 * InternalAuditStatus with identical values).
 */
type InsertInternalAuditReportTRPCInput = {
  Title: string;
  Status: Assessment_Status_Enum;
  OriginatingItemId?: string | null;
  Summary?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure so
 * consumers don't branch on which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertInternalAuditReportMutation => {
  return {
    insertInternalAuditReportApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertInternalAuditReportTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditReport.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.internalAuditReport.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.internalAuditReport.internalAuditReportCount.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.internalAuditReport.internalAuditReportsByOriginatingItemId.queryKey(),
        });
        // internal_audit_report and internal_audit_entity are the same table;
        // mirror the Apollo eviction of `internal_audit_entity` so the entity
        // register / by-id caches refresh too.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.internalAuditEntity.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.internalAuditEntity.internalAuditById.queryKey(),
        });
      },
    }),
  });

  return {
    insertInternalAuditReport: async (
      variables: InsertInternalAuditReportTRPCInput
    ): Promise<InsertInternalAuditReportMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
